// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

type NullableNumber = number | null | undefined;
type NullableString = string | null | undefined;

export enum ProjectReportEntryViewType {
    JOBTABLE = 'jobtable',
    USERTABLE = 'usertable',
}

export interface SerializedUserReportEntry {
    annotation_speed: number;
    annotating_time: number;
    user_id: number;
    user_name: string;
    draw_count: NullableNumber;
    delete_count: NullableNumber;
    update_count: NullableNumber;
    issue_count: number;
    score: number;
    frame_count: number;
}

export interface SerializedJobReportEntry {
    job_id: number;
    draw_count: NullableNumber;
    delete_count: NullableNumber;
    update_count: NullableNumber;
    issue_count: number;
    score: number;
    task_id: number;
    task_name: string;
    project_id: NullableNumber;
    project_name: NullableString;
    assignee_id: NullableNumber;
    assignee_name: NullableString;
    organization_id: NullableNumber;
    organization_name: NullableString;
    annotating_time: number;
    frame_count: number;
}

export interface SerializedProjectReport {
    report_users: SerializedUserReportEntry[];
    report_jobs: SerializedJobReportEntry[];
    created_date: string;
}

export interface SerializedJobReport {
    job_id: number;
    assignee: string;
    labeledshapes: number;
    stage: string;
    state: string;
}

export interface SerializedTaskReport {
    jobs: SerializedJobReport[];
}

export class UserReportEntry {
    annotationSpeed: number;
    annotatingTime: number;
    userId: number;
    userName: string;
    drawCount: NullableNumber;
    deleteCount: NullableNumber;
    updateCount: NullableNumber;
    issueCount: number;
    score: number;
    frameCount: number;
    constructor(userReport: SerializedUserReportEntry) {
        this.annotationSpeed = userReport.annotation_speed;
        this.annotatingTime = userReport.annotating_time;
        this.userId = userReport.user_id;
        this.userName = userReport.user_name;
        this.drawCount = userReport.draw_count;
        this.deleteCount = userReport.delete_count;
        this.updateCount = userReport.update_count;
        this.score = userReport.score;
        this.frameCount = userReport.frame_count;
        this.issueCount = userReport.issue_count;
    }
}

export class JobReportEntry {
    jobId: number;
    issuesCount: number;
    score: number;
    taskId: number;
    taskName: string;
    drawCount: NullableNumber;
    deleteCount: NullableNumber;
    updateCount: NullableNumber;
    projectId: NullableNumber;
    projectName: NullableString;
    assigneeId: NullableNumber;
    assigneeName: NullableString;
    organizationId: NullableNumber;
    organizationName: NullableString;
    annotatingTime: number;
    frameCount: number;

    constructor(jobReport: SerializedJobReportEntry) {
        this.jobId = jobReport.job_id;
        this.drawCount = jobReport.draw_count;
        this.deleteCount = jobReport.delete_count;
        this.updateCount = jobReport.update_count;
        this.issuesCount = jobReport.issue_count;
        this.score = jobReport.score;
        this.taskId = jobReport.task_id;
        this.taskName = jobReport.task_name;
        this.projectId = jobReport.project_id;
        this.projectName = jobReport.project_name;
        this.assigneeId = jobReport.assignee_id;
        this.assigneeName = jobReport.assignee_name;
        this.organizationId = jobReport.organization_id;
        this.organizationName = jobReport.organization_name;
        this.annotatingTime = jobReport.annotating_time;
        this.frameCount = jobReport.frame_count;
    }
}

export class ReportEntry {
    viewType: ProjectReportEntryViewType; // 标识这是哪一种类型的报告 （user还是job）
    jobContent: JobReportEntry[]; // 数据，如果是user则为空
    userContent: UserReportEntry[]; // 同上
    description: string; // 解释，不知道有啥用
    name: string; // 可以当个表名
    constructor(jobContent: JobReportEntry[], userContent: UserReportEntry[], viewType: ProjectReportEntryViewType) {
        this.viewType = viewType;
        this.userContent = userContent;
        this.jobContent = jobContent;
        if (viewType === ProjectReportEntryViewType.USERTABLE) {
            this.description = '标注员标注报告';
            this.name = 'ReportByUser';
        } else if (viewType === ProjectReportEntryViewType.JOBTABLE) {
            this.description = '作业标注报告';
            this.name = 'ReportByJob';
        }
    }
    public generateReportContent(): string[] {
        const contents: string[] = [];
        if (this.viewType === ProjectReportEntryViewType.USERTABLE) {
            for (let i = 0; i < this.userContent.length; i++) {
                const content = this.userContent[i];
                const line = `${content.userId},${content.userName},${content.frameCount},${content.drawCount},` +
                `${content.deleteCount},${content.updateCount},${content.issueCount},${content.score},` +
                `${content.annotatingTime},${content.annotationSpeed}\n`;
                contents.push(line);
            }
        } else {
            for (let i = 0; i < this.jobContent.length; i++) {
                const content = this.jobContent[i];
                const line = `${content.jobId},${content.taskId},${content.taskName},${content.projectId},` +
                `${content.projectName},${content.organizationId},${content.organizationName},${content.assigneeId},` +
                `${content.assigneeName},${content.frameCount},${content.drawCount},${content.deleteCount},` +
                `${content.updateCount},${content.issuesCount},${content.score},${content.annotatingTime}\n`;
                contents.push(line);
            }
        }
        return contents;
    }
}

/*
    结构应该是这样的：
    StatisticsEntry : 是一个分析单元，其中包括： viewType 和 content
    content是一个数组， 有两种类型：JobReportEntry 和 UserReportEntry，但一个ReportEntry中只有一种类型。
*/
export default class ProjectReport {
    #statistics: ReportEntry[]; // 应只有两个
    createdDate: string;
    #jobReportTitle: string;
    #userReportTitle: string;
    constructor(serializedProjectReport: SerializedProjectReport) {
        /*
            1. 首先解析出userReport 和 jobReport。
            2. 根据userReport 和 jobReport 构建StatisticsEntry
            3. 把StatisticsEntry push到 statistics中
        */
        const userReports: UserReportEntry[] = [];
        const jobReports: JobReportEntry[] = [];
        this.#statistics = [];
        for (const userReport of serializedProjectReport.report_users) {
            userReports.push(new UserReportEntry(userReport));
        }
        for (const jobReport of serializedProjectReport.report_jobs) {
            jobReports.push(new JobReportEntry(jobReport));
        }
        this.#statistics.push(new ReportEntry([], userReports, ProjectReportEntryViewType.USERTABLE));
        this.#statistics.push(new ReportEntry(jobReports, [], ProjectReportEntryViewType.JOBTABLE));
        this.createdDate = serializedProjectReport.created_date;
        this.#jobReportTitle = '作业ID,任务ID,任务名称,项目ID,项目名称,组织ID,组织名称,受托人ID,受托人名称,帧数量,创建标注数量,删除标注数量,更新标注数量,问题数量,标注合格率,标注时间（小时）\n';
        this.#userReportTitle = '用户ID,用户名,帧数量,创建标注数量,删除标注数量,更新标注数量,问题数量,标注合格率,标注时间（小时）,平均标注速度（标注数/标注时间）\n';
    }

    get statistics(): ReportEntry[] {
        return this.#statistics;
    }

    public downloadUserReport(): string[] {
        let csvUserReportContent: string[] = [];
        csvUserReportContent.push(this.#userReportTitle);
        for (let i = 0; i < this.#statistics.length; i++) {
            const statist = this.#statistics[i];
            if (statist.viewType === ProjectReportEntryViewType.USERTABLE) {
                csvUserReportContent = csvUserReportContent.concat(statist.generateReportContent());
            }
        }
        return csvUserReportContent;
    }
    public downloadJobReport(): string[] {
        let csvJobReportContent: string[] = [];
        csvJobReportContent.push(this.#jobReportTitle);
        for (let i = 0; i < this.#statistics.length; i++) {
            const statist = this.#statistics[i];
            if (statist.viewType === ProjectReportEntryViewType.JOBTABLE) {
                csvJobReportContent = csvJobReportContent.concat(statist.generateReportContent());
            }
        }
        return csvJobReportContent;
    }
}
